import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Upload, FileText, MessageSquare, Clock, Download,
  CheckCircle2, AlertTriangle, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  'Intake Started': 'bg-[#94A3B8] text-white',
  'Files Uploaded': 'bg-[#0B1F3F] text-white',
  'Received by VARfacts': 'bg-[#0B1F3F] text-white',
  'In Review': 'bg-[#C5A55A] text-[#0B1F3F]',
  'Clarification Needed': 'bg-amber-100 text-amber-800',
  'Report Drafting': 'bg-[#C5A55A]/20 text-[#0B1F3F]',
  'Final Review': 'bg-[#C5A55A]/40 text-[#0B1F3F]',
  'Delivered': 'bg-emerald-100 text-emerald-800',
  'Closed': 'bg-[#D4D4D0] text-[#64748B]',
};

const FILE_CATEGORIES = [
  'Exchange Export', 'Bank Statement', 'Wallet Record', 'Tax Document',
  'Screenshot', 'Correspondence', 'Legal Filing', 'Identity Document', 'Other',
];

export default function MatterDetail() {
  const { matterId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matter, setMatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [uploadCategory, setUploadCategory] = useState('Other');
  const [uploading, setUploading] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.portal_role === 'varfacts_admin' || user?.portal_role === 'varfacts_reviewer';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchMatter();
  }, [user, matterId]);

  const fetchMatter = async () => {
    try {
      const res = await axios.get(`${API}/portal/matters/${matterId}`, { withCredentials: true });
      setMatter(res.data);
    } catch (err) {
      toast.error('Matter not found');
      navigate('/portal');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', uploadCategory);
    try {
      await axios.post(`${API}/portal/matters/${matterId}/files`, formData, { withCredentials: true });
      toast.success('File uploaded');
      fetchMatter();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await axios.post(`${API}/portal/matters/${matterId}/comments`, {
        content: comment,
        internal_only: false,
      }, { withCredentials: true });
      setComment('');
      fetchMatter();
    } catch (err) {
      toast.error('Failed to post comment');
    }
  };

  const handleDownloadReport = () => {
    window.open(`${API}/portal/matters/${matterId}/report/download`, '_blank');
  };

  if (loading) return <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center"><p className="text-[#94A3B8]">Loading...</p></div>;
  if (!matter) return null;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="matter-detail-page">
      <nav className="bg-white border-b border-[#D4D4D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/portal" className="flex items-center gap-3">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" />
            <span className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8] hidden sm:inline">Secure Matter Portal</span>
          </Link>
          <Link to="/portal" className="text-sm text-[#64748B] hover:text-[#0B1F3F] flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {/* Matter Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="space-y-1">
            <p className="text-xs text-[#94A3B8] font-mono" data-testid="matter-id">{matter.matter_id}</p>
            <h1 className="font-heading text-2xl text-[#0B1F3F]" data-testid="matter-title">{matter.title}</h1>
            <p className="text-sm text-[#64748B]">{matter.client_name} &middot; {matter.matter_type}</p>
          </div>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[matter.status] || 'bg-[#D4D4D0]'}`} data-testid="matter-status">
            {matter.status}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            {matter.description && (
              <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Description</p>
                <p className="text-sm text-[#64748B] leading-relaxed">{matter.description}</p>
              </div>
            )}

            {/* File Upload */}
            <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Files ({matter.files?.length || 0})</p>
                <div className="flex items-center gap-2">
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="text-xs border border-[#D4D4D0] rounded px-2 py-1"
                  >
                    {FILE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <label className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-[#0B1F3F] text-white rounded-sm cursor-pointer hover:bg-[#0B1F3F]/90">
                    <Upload className="w-3 h-3" />
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} data-testid="file-upload-input" />
                  </label>
                </div>
              </div>
              {matter.files?.length > 0 ? (
                <div className="space-y-2">
                  {matter.files.map((f) => (
                    <div key={f.file_id} className="flex items-center justify-between border border-[#D4D4D0] rounded-sm p-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-[#C5A55A]" />
                        <div>
                          <p className="text-sm text-[#0B1F3F]">{f.original_name}</p>
                          <p className="text-xs text-[#94A3B8]">{f.category} &middot; {(f.file_size / 1024).toFixed(0)} KB</p>
                        </div>
                      </div>
                      <a
                        href={`${API}/portal/matters/${matterId}/files/${f.file_id}/download`}
                        className="text-xs text-[#C5A55A] hover:text-[#0B1F3F]"
                        data-testid={`download-${f.file_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#94A3B8]">No files uploaded yet.</p>
              )}
            </div>

            {/* Report Download */}
            {matter.status === 'Delivered' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 space-y-3" data-testid="report-section">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <p className="font-semibold text-emerald-800">VARfacts Report Delivered</p>
                </div>
                <Button onClick={handleDownloadReport} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-sm text-sm">
                  <Download className="w-4 h-4 mr-2" /> Download Report
                </Button>
              </div>
            )}

            {/* Comments */}
            <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Messages</p>
              {matter.comments?.length > 0 ? (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {[...matter.comments].reverse().map((c) => (
                    <div key={c.comment_id} className={`border rounded-sm p-3 ${c.internal_only ? 'border-amber-200 bg-amber-50' : 'border-[#D4D4D0]'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-[#0B1F3F]">
                          {c.author_name}
                          {c.internal_only && <span className="ml-2 text-amber-600">(Internal)</span>}
                        </p>
                        <p className="text-xs text-[#94A3B8]">{new Date(c.created_at).toLocaleString()}</p>
                      </div>
                      <p className="text-sm text-[#64748B] whitespace-pre-wrap">{c.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#94A3B8]">No messages yet.</p>
              )}
              <form onSubmit={handleComment} className="flex gap-2 pt-2">
                <Input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a message..."
                  className="flex-1 h-10 bg-white border-[#D4D4D0]"
                  data-testid="comment-input"
                />
                <Button type="submit" size="sm" className="btn-primary-varfacts px-4 h-10 rounded-sm" data-testid="send-comment-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Matter Info */}
            <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Matter Details</p>
              <div className="space-y-3 text-sm">
                {matter.opposing_party && (
                  <div><p className="text-[#94A3B8] text-xs">Opposing Party</p><p className="text-[#0B1F3F]">{matter.opposing_party}</p></div>
                )}
                {matter.court_reference && (
                  <div><p className="text-[#94A3B8] text-xs">Court Reference</p><p className="text-[#0B1F3F]">{matter.court_reference}</p></div>
                )}
                <div><p className="text-[#94A3B8] text-xs">Created</p><p className="text-[#0B1F3F]">{new Date(matter.created_at).toLocaleDateString()}</p></div>
              </div>
            </div>

            {/* Status History */}
            <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Status History</p>
              <div className="space-y-3">
                {matter.status_history?.map((evt) => (
                  <div key={evt.event_id} className="flex items-start gap-3">
                    <Clock className="w-3.5 h-3.5 text-[#C5A55A] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-[#0B1F3F]">{evt.status}</p>
                      {evt.note && <p className="text-xs text-[#64748B]">{evt.note}</p>}
                      <p className="text-xs text-[#94A3B8]">{new Date(evt.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
